import { useState, useEffect, CSSProperties } from "react";
import "./OwDateInput.scss";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useIntl } from "react-intl";
import * as _ from "lodash-es";

import enUS from "date-fns/locale/en-US";
import enGB from "date-fns/locale/en-GB";
import de from "date-fns/locale/de";
import moment, { Moment } from "moment-timezone";
registerLocale("en-GB", enGB);
registerLocale("en-US", enUS);
registerLocale("de", de);

type Props = {
    selectedDate?: any;
    fn: (date: Moment) => void;
    className?: string;
    disabled?: boolean;
    datePickerClassName?: string;
    popperPlacement?: any;
    popperModifiers?: any;
    noZIndex?: boolean;
    isUTC?: boolean;
    locale?: string;
    dateFormat?: string;
    iconClassName?: string;
    endDate?: Date;
    filterDate?: (date: Date) => boolean;
};

const OwDateInput = (props: Props) => {
    const intl = useIntl();
    const { isUTC, fn: callbackFn } = props;

    const handleSelectedDate = () => {
        let _selectedDate = props.selectedDate;

        if (moment.isMoment(_selectedDate)) {
            if (props.isUTC) {
                // For UTC moments, create a local date with the same day/month/year
                const utcMoment = _selectedDate.utc();
                _selectedDate = new Date(utcMoment.year(), utcMoment.month(), utcMoment.date());
            } else {
                _selectedDate = _selectedDate.toDate();
            }
        }

        if (_.isString(_selectedDate)) {
            // Parse date strings without timezone conversion to prevent day shift
            if (props.isUTC) {
                // For UTC strings, create a local date with the same day/month/year
                const utcMoment = moment.utc(_selectedDate);
                _selectedDate = new Date(utcMoment.year(), utcMoment.month(), utcMoment.date());
            } else {
                // For plain date strings (YYYY-MM-DD), extract components directly to avoid timezone issues
                const m = moment(_selectedDate, ["YYYY-MM-DD", moment.ISO_8601]);
                _selectedDate = new Date(m.year(), m.month(), m.date());
            }
        }

        // const style: CSSProperties = {};
        //
        // if (!props.noZIndex) {
        //     style.zIndex = 1;
        // }

        if (!_selectedDate || _selectedDate.toString() === "Invalid Date") {
            _selectedDate = null;
        }

        return _selectedDate;
    };

    const [selectedDate, setSelectedDate] = useState<Date | null>(handleSelectedDate());
    const [style, setStyle] = useState<CSSProperties>({});
    const locale = props.locale || intl.locale;
    const dateFormat = props.dateFormat || "yyyy-MM-dd";

    // Sync internal state when props.selectedDate changes
    useEffect(() => {
        const newDate = handleSelectedDate();
        setSelectedDate(newDate);
    }, [props.selectedDate]);

    // Track if the change is from internal state update
    const [isInternalChange, setIsInternalChange] = useState(false);

    useEffect(() => {
        // Only call callbackFn if this is an internal change (user interaction)
        if (isInternalChange && selectedDate !== null) {
            if (isUTC) {
                callbackFn(moment(selectedDate).utc(false));
            } else {
                // Create moment from date components to avoid timezone issues
                const d = selectedDate;
                callbackFn(moment([d.getFullYear(), d.getMonth(), d.getDate()]));
            }
            setIsInternalChange(false);
        }
    }, [selectedDate, isInternalChange, isUTC, callbackFn]);

    const fn = (date: Date | null) => {
        setIsInternalChange(true);
        setSelectedDate(date);
    };

    const onBlur = () => {
        setStyle({
            zIndex: 0,
        });
    };

    const onFocus = () => {
        setStyle({
            zIndex: 1,
        });
    };

    return (
        <div className={"OwDateInput d-flex input-group w-100 mb-2 me-sm-2 no-wrap " + (props.className || "")} style={style}>
            <DatePicker
                locale={locale}
                className={"d-inline-flex form-control form-control-sm rounded-end-0 " + props.datePickerClassName}
                selected={selectedDate}
                dateFormat={dateFormat}
                onFocus={onFocus}
                onBlur={onBlur}
                onSelect={fn}
                onChange={fn}
                disabled={props.disabled}
                popperPlacement={props.popperPlacement}
                popperModifiers={props.popperModifiers}
                endDate={props.endDate}
                filterDate={props.filterDate}
            />
            <div className={props.iconClassName}>
                <div className="input-group-text rounded-start-0 h-100">
                    <i className="fas fa-calendar-alt" />
                </div>
            </div>
        </div>
    );
};

export default OwDateInput;
